import React from "react";
import { motion } from "framer-motion";
import blogImage1 from "../../assets/blogs/strength_for_longativity/b1.jpg";
import blogImage2 from "../../assets/blogs/strength_for_longativity/b2.jpg";
import blogImage3 from "../../assets/blogs/strength_for_longativity/b3.jpg";
import RecentPosts from "../../components/blogs/RecentPosts";
import Footer from "../../components/homepage/Footer"

export default function StrengthLongevityBlog() {
  return (
    <>
    <div className="flex max-w-[1200px] mx-auto gap-10 py-16 px-4">

      {/* Left Blog Content WITH BLACK PANEL */}
      <motion.div
        className="w-full lg:w-3/4 bg-black/30 p-10 rounded-xl flex flex-col gap-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >

        {/* Main Image with overlay */}
        <div className="relative w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-lg">
          <img
            src={blogImage1}
            alt="Endurance vs Strength"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 w-full bg-black/20 p-6 text-white flex flex-col gap-2">
            <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">
              Endurance vs Strength for Longevity
            </h1>
            <p className="text-gray-200 text-lg">Kalon | November 26, 2025</p>
          </div>
        </div>

        {/* Blog Content */}
        <motion.div
          className="flex flex-col gap-6 text-gray-200 text-lg leading-relaxed"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            Physical activity is one of the most powerful determinants of longevity. While many people debate whether endurance or strength training is better, the reality is that both have unique benefits that contribute to longer, healthier life.
          </p>

          <h2 className="text-2xl font-semibold text-neonTurquoise mt-6">1. Endurance Training</h2>
          <p>
            Endurance exercises, such as running, cycling, swimming, and hiking, primarily improve cardiovascular fitness. They help increase oxygen delivery to tissues, lower resting heart rate, and reduce the risk of chronic diseases like hypertension, diabetes, and heart disease.
          </p>
          <ul className="list-disc list-inside ml-4">
            <li>Improves heart and lung capacity</li>
            <li>Enhances metabolism and fat burning</li>
            <li>Reduces inflammation markers</li>
            <li>Supports mental health and cognitive function</li>
          </ul>

          {/* Secondary Image */}
          <motion.img
            src={blogImage2}
            alt="Endurance Training"
            className="rounded-xl w-full object-cover h-[350px] shadow-lg"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          />

          <h2 className="text-2xl font-semibold text-neonTurquoise mt-6">2. Strength Training</h2>
          <p>
            Strength or resistance training focuses on building muscle mass, bone density, and functional strength. Strong muscles protect joints, improve posture, and enhance overall physical resilience.
          </p>
          <ul className="list-disc list-inside ml-4">
            <li>Preserves lean muscle mass as we age</li>
            <li>Enhances metabolic rate, even at rest</li>
            <li>Supports joint health and mobility</li>
            <li>Reduces risk of osteoporosis and sarcopenia</li>
          </ul>

          <h2 className="text-2xl font-semibold text-neonTurquoise mt-6">3. Comparing Longevity Benefits</h2>
          <p>
            While endurance improves cardiovascular health and stamina, strength training enhances musculoskeletal health. Studies indicate that combining both modalities offers the greatest longevity benefits.
          </p>

          <table className="w-full text-left border border-gray-700 rounded-xl overflow-hidden">
            <thead className="bg-neonTurquoise/20 text-white">
              <tr>
                <th className="px-4 py-2">Training Type</th>
                <th className="px-4 py-2">Primary Benefits</th>
                <th className="px-4 py-2">Longevity Impact</th>
              </tr>
            </thead>
            <tbody className="bg-gray-900 text-gray-200">
              <tr className="hover:bg-gray-800 transition">
                <td className="px-4 py-2">Endurance</td>
                <td className="px-4 py-2">Heart, lungs, stamina</td>
                <td className="px-4 py-2">Improved cardiovascular longevity</td>
              </tr>
              <tr className="hover:bg-gray-800 transition">
                <td className="px-4 py-2">Strength</td>
                <td className="px-4 py-2">Muscle, bones, joints</td>
                <td className="px-4 py-2">Reduced frailty, improved functional longevity</td>
              </tr>
              <tr className="hover:bg-gray-800 transition">
                <td className="px-4 py-2">Combined</td>
                <td className="px-4 py-2">All of the above</td>
                <td className="px-4 py-2">Maximized lifespan and healthspan</td>
              </tr>
            </tbody>
          </table>

          <h2 className="text-2xl font-semibold text-neonTurquoise mt-6">4. Weekly Training Plan Example</h2>
          <ol className="list-decimal list-inside ml-4">
            <li>Monday: Endurance run or cycling (45–60 min)</li>
            <li>Tuesday: Strength training – full body</li>
            <li>Wednesday: Active recovery – yoga or hiking</li>
            <li>Thursday: Endurance intervals</li>
            <li>Friday: Strength training – lower body & core</li>
            <li>Saturday: Mixed activity – hike, swim, or sport</li>
            <li>Sunday: Rest or mobility work</li>
          </ol>

          <motion.img
            src={blogImage3}
            alt="Strength Training"
            className="rounded-xl w-full object-cover h-[350px] shadow-lg"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          />

          <h2 className="text-2xl font-semibold text-neonTurquoise mt-6">5. Key Takeaways</h2>
          <ul className="list-disc list-inside ml-4">
            <li>Both endurance and strength training support longevity.</li>
            <li>Endurance improves cardiovascular performance.</li>
            <li>Strength protects muscle, bone, and joints.</li>
            <li>A combined routine maximizes long-term health.</li>
            <li>Consistency and recovery remain essential.</li>
          </ul>

          <p className="mt-4">
            By integrating endurance and strength training into your weekly routine, you not only extend lifespan but also enhance quality of life, mobility, and independence as you age.
          </p>
        </motion.div>
      </motion.div>

      {/* Right Fixed Sidebar */}
      <div className="hidden lg:block lg:w-1/4 sticky top-20 self-start">
        <RecentPosts />
      </div>
    </div>
    <Footer />

    </>
  );
}
